import { useState } from 'react';
import {
  ArrowDownLeft, ArrowUpRight, Banknote, Plus, Printer, RefreshCw, Scale,
} from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountTransactionsQuery, useAccountsQuery, useAdjustAccountMutation,
  useCreateAccountMutation, useDailyBalanceQuery, useDeleteAccountMutation,
  useMoveAccountCashMutation, usePaymentMethodsQuery, useUpdateAccountMutation,
  useUpdatePaymentMethodMutation,
  type AccountTransaction, type FinancialAccount,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatDateTime, formatMoney, isoDate } from '@/lib/format';
import {
  Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, LoadingState,
  Modal, PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { ExportMenu } from '@/components/ExportMenu';
import { SummaryCards } from '@/components/SummaryCards';
import { CashMovementReceipt } from '@/components/CashMovementReceipt';

const TYPE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  BANK: 'Bank',
  MOBILE_MONEY: 'Mobile money',
  OTHER: 'Other',
};



/**
 * Where the money is, and whether the day balances.
 *
 * Every balance on this page is a projection of the cash ledger — the same
 * arrangement stock uses. Nothing here edits a balance directly: a figure that
 * disagrees with what was counted is corrected by posting the difference, so a
 * short drawer stays on the record instead of quietly becoming right.
 */
export function AccountsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [date, setDate] = useState(isoDate());
  const [allDates, setAllDates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [counting, setCounting] = useState<FinancialAccount | null>(null);
  // Opening an account shows only its own movements, which is how anyone
  // actually reads a drawer or a bank statement.
  const [viewing, setViewing] = useState<FinancialAccount | null>(null);
  const [removing, setRemoving] = useState<FinancialAccount | null>(null);
  const [inspecting, setInspecting] = useState<AccountTransaction | null>(null);
  const [slipFor, setSlipFor] = useState<AccountTransaction | null>(null);
  const [moving, setMoving] = useState<{ account: FinancialAccount; direction: 'deposit' | 'withdraw' } | null>(null);
  const [move, setMove] = useState({ amount: '', reason: '', reference: '' });
  const [failure, setFailure] = useState<string | null>(null);

  const accounts = useAccountsQuery({ limit: 50, includeClosed: true });
  const daily = useDailyBalanceQuery({ date });
  // The ledger answers the same day the panel above is balancing. A date
  // filter that moved only half the page was the confusing part.
  const movements = useAccountTransactionsQuery({
    page,
    limit: 15,
    ...(viewing ? { accountId: viewing.id } : {}),
    ...(allDates ? {} : { from: date, to: date }),
  });
  const methods = usePaymentMethodsQuery();

  const [createAccount, createState] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const [adjustAccount, adjustState] = useAdjustAccountMutation();
  const [updateMethod] = useUpdatePaymentMethodMutation();
  const [deleteAccount, deleteState] = useDeleteAccountMutation();
  const [moveCash, moveState] = useMoveAccountCashMutation();

  const [form, setForm] = useState({ name: '', type: 'CASH', accountNumber: '', openingBalance: '' });
  const [counted, setCounted] = useState({ amount: '', reason: '' });

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await createAccount({
        name: form.name,
        type: form.type,
        accountNumber: form.accountNumber || undefined,
        openingBalance: form.openingBalance ? Number(form.openingBalance) : undefined,
      }).unwrap();
      setCreating(false);
      setForm({ name: '', type: 'CASH', accountNumber: '', openingBalance: '' });
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That account could not be opened'));
    }
  };

  const postCount = async (): Promise<void> => {
    if (!counting) return;
    setFailure(null);
    try {
      await adjustAccount({
        id: counting.id,
        countedBalance: Number(counted.amount),
        reason: counted.reason,
      }).unwrap();
      setCounting(null);
      setCounted({ amount: '', reason: '' });
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That correction could not be posted'));
    }
  };

  /** Every figure on this page is read from the ledger, so all four reads go. */
  const refreshing =
    accounts.isFetching || daily.isFetching || movements.isFetching || methods.isFetching;

  const refresh = async (): Promise<void> => {
    setFailure(null);
    await Promise.all([
      accounts.refetch(),
      daily.refetch(),
      movements.refetch(),
      methods.refetch(),
    ]);
  };

  const linkMethod = async (methodId: string, accountId: string): Promise<void> => {
    setFailure(null);
    try {
      await updateMethod({ id: methodId, body: { accountId: accountId || null } }).unwrap();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That method could not be linked'));
    }
  };

  const postMove = async (): Promise<void> => {
    if (!moving) return;
    setFailure(null);
    try {
      await moveCash({
        id: moving.account.id,
        direction: moving.direction,
        amount: Number(move.amount),
        reason: move.reason,
        reference: move.reference || undefined,
      }).unwrap();
      setMoving(null);
      setMove({ amount: '', reason: '', reference: '' });
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That movement could not be posted'));
    }
  };

  const remove = async (): Promise<void> => {
    if (!removing) return;
    setFailure(null);
    try {
      await deleteAccount(removing.id).unwrap();
      setRemoving(null);
      if (viewing?.id === removing.id) setViewing(null);
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That account could not be deleted'));
    }
  };

  const close = async (account: FinancialAccount): Promise<void> => {
    setFailure(null);
    try {
      await updateAccount({ id: account.id, body: { isActive: !account.isActive } }).unwrap();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That account could not be changed'));
    }
  };

  return (
    <>
      <PageHeader
        title="Accounts"
        description="Cash, bank and mobile-money accounts. Every payment and expense moves through one of them."
        action={
          <div className="flex gap-2">
            {/* Money moves from the till, the purchasing screen and the expense
                queue, so this page goes stale while it is open. */}
            <Button
              variant="outline"
              icon={RefreshCw}
              loading={refreshing}
              onClick={() => void refresh()}
            >
              Refresh
            </Button>
            <Can permission={P.ACCOUNT_MANAGE}>
              <Button icon={Plus} onClick={() => setCreating(true)}>New account</Button>
            </Can>
          </div>
        }
      />

      {failure && <div className="mb-4"><Alert tone="danger">{failure}</Alert></div>}

      {daily.data && (
        <SummaryCards
          stats={[
            {
              label: 'Cash on hand',
              value: formatMoney(
                accounts.data?.items
                  .filter((account) => account.isActive)
                  .reduce((sum, account) => sum + Number(account.balance), 0)
                  .toFixed(2) ?? '0',
                format,
              ),
              hint: 'across every open account, right now',
            },
            {
              label: 'In',
              value: formatMoney(daily.data.totals.moneyIn, format),
              hint: `on ${formatDate(date, format)}`,
            },
            {
              label: 'Out',
              value: formatMoney(daily.data.totals.moneyOut, format),
              hint: `on ${formatDate(date, format)}`,
            },
            {
              label: 'Should hold',
              value: formatMoney(daily.data.totals.closing, format),
              hint: 'at the end of that day',
            },
          ]}
        />
      )}

      <Card className="mb-6">
        <CardHeader
          title="Day end — count your cash"
          description="What each account opened with, what moved, and what it should hold now. Count the real money and post any difference."
          action={
            <Input
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                setPage(1);
              }}
              aria-label="Day to balance"
            />
          }
        />

        {daily.isLoading ? (
          <LoadingState />
        ) : daily.isError ? (
          <ErrorState message={apiErrorMessage(daily.error)} onRetry={() => void daily.refetch()} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th numeric>Opening</Th>
                <Th numeric>In</Th>
                <Th numeric>Out</Th>
                <Th numeric>Should hold</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {daily.data?.accounts.map((row) => (
                <tr key={row.accountId}>
                  <Td>
                    <button
                      type="button"
                      className="font-medium hover:underline"
                      onClick={() => {
                        const account = accounts.data?.items.find((a) => a.id === row.accountId);
                        if (account) {
                          setViewing(account);
                          setPage(1);
                        }
                      }}
                    >
                      {row.name}
                    </button>
                    <span className="ml-2 text-xs text-muted">{TYPE_LABELS[row.type] ?? row.type}</span>
                  </Td>
                  <Td numeric className="text-secondary">{formatMoney(row.opening, format)}</Td>
                  <Td numeric className="text-emerald-600 dark:text-emerald-400">
                    {formatMoney(row.moneyIn, format)}
                  </Td>
                  <Td numeric className="text-red-600 dark:text-red-400">
                    {formatMoney(row.moneyOut, format)}
                  </Td>
                  <Td numeric className="font-medium">{formatMoney(row.closing, format)}</Td>
                  <Td className="text-right">
                    <Can permission={P.ACCOUNT_MANAGE}>
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={ArrowDownLeft}
                          onClick={() => {
                            const account = accounts.data?.items.find((a) => a.id === row.accountId);
                            if (account) {
                              setMoving({ account, direction: 'deposit' });
                              setMove({ amount: '', reason: '', reference: '' });
                            }
                          }}
                        >
                          Deposit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={ArrowUpRight}
                          onClick={() => {
                            const account = accounts.data?.items.find((a) => a.id === row.accountId);
                            if (account) {
                              setMoving({ account, direction: 'withdraw' });
                              setMove({ amount: '', reason: '', reference: '' });
                            }
                          }}
                        >
                          Withdraw
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={Scale}
                          onClick={() => {
                            const account = accounts.data?.items.find((a) => a.id === row.accountId);
                            if (account) {
                              setCounting(account);
                              setCounted({ amount: row.closing, reason: '' });
                            }
                          }}
                        >
                          Count
                        </Button>
                      </div>
                    </Can>
                  </Td>
                </tr>
              ))}
              {daily.data && daily.data.accounts.length > 0 && (
                <tr className="border-t border-subtle font-medium">
                  <Td>All accounts</Td>
                  <Td numeric>{formatMoney(daily.data.totals.opening, format)}</Td>
                  <Td numeric>{formatMoney(daily.data.totals.moneyIn, format)}</Td>
                  <Td numeric>{formatMoney(daily.data.totals.moneyOut, format)}</Td>
                  <Td numeric>{formatMoney(daily.data.totals.closing, format)}</Td>
                  <Td />
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="mb-6">
        <CardHeader title="Accounts" description="Money can only move through an open account." />

        {accounts.isLoading ? (
          <LoadingState />
        ) : accounts.data?.items.length === 0 ? (
          <EmptyState
            icon={Banknote}
            title="No accounts yet"
            description="Open one for the till, and one for each bank or mobile-money account."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Kind</Th>
                <Th>Pays into</Th>
                <Th numeric>Balance</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {accounts.data?.items.map((account) => (
                <tr key={account.id} className={account.isActive ? undefined : 'opacity-60'}>
                  <Td>
                    <button
                      type="button"
                      className="font-medium hover:underline"
                      onClick={() => {
                        setViewing(account);
                        setPage(1);
                      }}
                    >
                      {account.name}
                    </button>
                    {!account.isActive && <Badge tone="neutral" className="ml-2">closed</Badge>}
                    {account.accountNumber && (
                      <p className="text-xs text-muted">{account.accountNumber}</p>
                    )}
                  </Td>
                  <Td className="text-secondary">{TYPE_LABELS[account.type] ?? account.type}</Td>
                  <Td className="text-secondary">
                    {account._count?.paymentMethods
                      ? `${account._count.paymentMethods} method${account._count.paymentMethods === 1 ? '' : 's'}`
                      : <span className="text-muted">no method</span>}
                  </Td>
                  <Td numeric className="font-medium">{formatMoney(account.balance, format)}</Td>
                  <Td className="text-right">
                    <Can permission={P.ACCOUNT_MANAGE}>
                      <Button size="sm" variant="ghost" onClick={() => void close(account)}>
                        {account.isActive ? 'Close' : 'Reopen'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(account)}>
                        Delete
                      </Button>
                    </Can>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="mb-6">
        <CardHeader
          title="Payment methods"
          description="Where the money from each method lands. A method with no account cannot take payment."
        />

        {methods.isLoading ? (
          <LoadingState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Method</Th>
                <Th>Kind</Th>
                <Th numeric>Fee</Th>
                <Th>Lands in</Th>
              </tr>
            </thead>
            <tbody>
              {methods.data?.map((method) => {
                // Credit moves no cash — the customer's balance carries it — so
                // it is the one kind that is complete without an account.
                const movesCash = method.type !== 'CREDIT' && method.type !== 'STORE_CREDIT';
                return (
                  <tr key={method.id}>
                    <Td>
                      <span className="font-medium">{method.name}</span>
                      {method.isDefault && <Badge tone="brand" className="ml-2">default</Badge>}
                    </Td>
                    <Td className="text-secondary">
                      {method.type.toLowerCase().replaceAll('_', ' ')}
                    </Td>
                    <Td numeric className="text-secondary">
                      {Number(method.feePercent) > 0 ? `${method.feePercent}%` : '—'}
                    </Td>
                    <Td>
                      {!movesCash ? (
                        <span className="text-xs text-muted">no cash moves</span>
                      ) : (
                        <Can
                          permission={P.ACCOUNT_MANAGE}
                          fallback={
                            <span className={method.account ? undefined : 'text-red-600'}>
                              {method.account?.name ?? 'not linked'}
                            </span>
                          }
                        >
                          <Select
                            aria-label={`Account for ${method.name}`}
                            value={method.accountId ?? ''}
                            onChange={(event) => void linkMethod(method.id, event.target.value)}
                          >
                            <option value="">Not linked</option>
                            {accounts.data?.items
                              .filter((account) => account.isActive)
                              .map((account) => (
                                <option key={account.id} value={account.id}>{account.name}</option>
                              ))}
                          </Select>
                        </Can>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title={viewing ? `${viewing.name} — transactions` : 'Cash ledger'}
          description={
            allDates
              ? `Every movement${viewing ? ` through ${viewing.name}` : ''}, newest first.`
              : `What moved on ${formatDate(date, format)}${viewing ? ` through ${viewing.name}` : ''}.`
          }
          action={
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAllDates((current) => !current);
                  setPage(1);
                }}
              >
                {allDates ? 'Just this day' : 'All dates'}
              </Button>
              {viewing && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setViewing(null); setPage(1); }}
                >
                  All accounts
                </Button>
              )}
              {viewing && (
                // A statement is one account's: with every account mixed
                // together there is no running balance to print.
                <ExportMenu
                  size="sm"
                  period={allDates ? 'every entry to date' : formatDate(date, format)}
                  items={[
                    {
                      label: `${viewing.name} statement`,
                      path: '/reports/accounts/export',
                      params: {
                        accountId: viewing.id,
                        from: allDates ? undefined : date,
                        to: allDates ? undefined : date,
                      },
                      formats: ['pdf', 'xlsx'],
                    },
                  ]}
                />
              )}
            </div>
          }
        />

        {movements.isLoading ? (
          <LoadingState />
        ) : movements.data?.items.length === 0 ? (
          <EmptyState
            icon={Banknote}
            title="No money has moved yet"
            description="Payments, expenses and corrections all appear here."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Account</Th>
                  <Th>Movement</Th>
                  <Th numeric>Amount</Th>
                  <Th numeric>Balance after</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {movements.data?.items.map((entry) => (
                  <tr
                    key={entry.id}
                    className="cursor-pointer hover:bg-sunken"
                    onClick={() => setInspecting(entry)}
                  >
                    <Td className="whitespace-nowrap text-xs text-muted">
                      {formatDateTime(entry.occurredAt, format)}
                    </Td>
                    <Td className="text-secondary">{entry.account.name}</Td>
                    <Td>
                      <span className="text-sm">{entry.type.toLowerCase().replaceAll('_', ' ')}</span>
                      {(entry.referenceNumber || entry.notes) && (
                        <p className="text-xs text-muted">{entry.referenceNumber ?? entry.notes}</p>
                      )}
                    </Td>
                    <Td
                      numeric
                      className={
                        entry.direction === 'IN'
                          ? 'font-medium text-emerald-600 dark:text-emerald-400'
                          : 'font-medium text-red-600 dark:text-red-400'
                      }
                    >
                      {entry.direction === 'IN' ? '+' : '−'}{formatMoney(entry.amount, format)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {formatMoney(entry.balanceAfter, format)}
                    </Td>
                    <Td className="text-right">
                      {/* Every line is something someone can be asked to
                          account for, so every line can be printed. */}
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Printer}
                        aria-label={`Print slip for ${entry.type.toLowerCase().replaceAll('_', ' ')}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSlipFor(entry);
                        }}
                      >
                        Slip
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {movements.data && (
              <Pagination
                page={movements.data.meta.page}
                totalPages={movements.data.meta.totalPages}
                total={movements.data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {creating && (
        <Modal
          open
          onClose={() => setCreating(false)}
          title="New account"
          description="A till, a bank account, or a mobile-money wallet."
          footer={
            <>
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button
                loading={createState.isLoading}
                disabled={!form.name}
                onClick={() => void submit()}
              >
                Open account
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Input
              label="Name"
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Main Till"
            />
            <Select
              label="Kind"
              value={form.type}
              onChange={(event) => setForm({ ...form, type: event.target.value })}
            >
              <option value="CASH">Cash</option>
              <option value="BANK">Bank</option>
              <option value="MOBILE_MONEY">Mobile money</option>
              <option value="OTHER">Other</option>
            </Select>
            <Input
              label="Account number"
              value={form.accountNumber}
              onChange={(event) => setForm({ ...form, accountNumber: event.target.value })}
              hint="For a bank or mobile-money account."
            />
            <Input
              label="Opening balance"
              type="number"
              min="0"
              step="0.01"
              value={form.openingBalance}
              onChange={(event) => setForm({ ...form, openingBalance: event.target.value })}
              hint="What it holds today. Posted as the first entry in its history."
            />
          </div>
        </Modal>
      )}

      {inspecting && (
        <Modal
          open
          onClose={() => setInspecting(null)}
          title={inspecting.type.toLowerCase().replaceAll('_', ' ')}
          description={`${inspecting.account.name} · ${formatDateTime(inspecting.occurredAt, format)}`}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setInspecting(null)}>Close</Button>
              <Button
                icon={Printer}
                onClick={() => {
                  setSlipFor(inspecting);
                  setInspecting(null);
                }}
              >
                Print slip
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-subtle p-4 text-center">
              <p
                className={
                  inspecting.direction === 'IN'
                    ? 'text-2xl font-semibold text-emerald-600 dark:text-emerald-400'
                    : 'text-2xl font-semibold text-red-600 dark:text-red-400'
                }
              >
                {inspecting.direction === 'IN' ? '+' : '−'}
                {formatMoney(inspecting.amount, format)}
              </p>
              {/* Before and after are what make an entry checkable: the balance
                  it left behind should be the one the next entry starts from. */}
              <p className="mt-1 text-sm text-muted">
                {formatMoney(inspecting.balanceBefore, format)} →{' '}
                <span className="font-medium text-secondary">
                  {formatMoney(inspecting.balanceAfter, format)}
                </span>
              </p>
            </div>

            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted">Account</dt>
              <dd className="text-right">{inspecting.account.name}</dd>

              <dt className="text-muted">Movement</dt>
              <dd className="text-right">
                {inspecting.type.toLowerCase().replaceAll('_', ' ')}
                {' · '}
                {inspecting.direction === 'IN' ? 'money in' : 'money out'}
              </dd>

              <dt className="text-muted">Source</dt>
              <dd className="text-right">
                {inspecting.referenceType.toLowerCase().replaceAll('_', ' ')}
                {inspecting.referenceNumber && (
                  <span className="block font-mono text-xs">{inspecting.referenceNumber}</span>
                )}
              </dd>

              <dt className="text-muted">Happened</dt>
              <dd className="text-right">{formatDateTime(inspecting.occurredAt, format)}</dd>

              {inspecting.createdAt && inspecting.createdAt !== inspecting.occurredAt && (
                <>
                  <dt className="text-muted">Recorded</dt>
                  <dd className="text-right">{formatDateTime(inspecting.createdAt, format)}</dd>
                </>
              )}

              <dt className="text-muted">Recorded by</dt>
              <dd className="text-right">
                {inspecting.createdBy
                  ? `${inspecting.createdBy.firstName} ${inspecting.createdBy.lastName}`
                  : 'the system'}
              </dd>
            </dl>

            {inspecting.notes && (
              <div>
                <p className="mb-1 text-sm text-muted">Note</p>
                <p className="rounded-lg border border-subtle p-3 text-sm">{inspecting.notes}</p>
              </div>
            )}

            <p className="text-xs text-muted">
              Ledger entries are never edited. A movement that was wrong is corrected by
              posting another.
            </p>
          </div>
        </Modal>
      )}

      {slipFor && (
        <CashMovementReceipt entry={slipFor} onClose={() => setSlipFor(null)} />
      )}

      {moving && (
        <Modal
          open
          onClose={() => setMoving(null)}
          title={
            moving.direction === 'deposit'
              ? `Put money into ${moving.account.name}`
              : `Take money out of ${moving.account.name}`
          }
          description={
            moving.direction === 'deposit'
              ? 'A float, capital, or cash brought back. Not a payment against a sale.'
              : 'Banking the till, a draw, or topping up petty cash.'
          }
          size="sm"
          footer={
            <>
              <Button variant="outline" onClick={() => setMoving(null)}>Cancel</Button>
              <Button
                loading={moveState.isLoading}
                disabled={!(Number(move.amount) > 0) || !move.reason}
                onClick={() => void postMove()}
              >
                {moving.direction === 'deposit' ? 'Post deposit' : 'Post withdrawal'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Alert tone="info">
              {moving.account.name} holds {formatMoney(moving.account.balance, format)}.
            </Alert>
            <Input
              label="Amount"
              type="number"
              min="0"
              step="0.01"
              value={move.amount}
              onChange={(event) => setMove({ ...move, amount: event.target.value })}
            />
            <Input
              label="Reason"
              required
              value={move.reason}
              onChange={(event) => setMove({ ...move, reason: event.target.value })}
              placeholder={
                moving.direction === 'deposit' ? 'Opening float for the day' : 'Banked at Stanbic'
              }
            />
            <Input
              label="Reference"
              value={move.reference}
              onChange={(event) => setMove({ ...move, reference: event.target.value })}
              hint="Slip or transaction number, if there is one."
            />
          </div>
        </Modal>
      )}

      {removing && (() => {
        const counts = removing._count;
        const used =
          (counts?.transactions ?? 0) + (counts?.payments ?? 0)
          + (counts?.expenses ?? 0) + (counts?.purchaseOrders ?? 0);
        const linked = counts?.paymentMethods ?? 0;
        const blocked = used > 0 || linked > 0;

        return (
          <Modal
            open
            onClose={() => { setRemoving(null); setFailure(null); }}
            title={`Delete ${removing.name}?`}
            size="sm"
            footer={
              <>
                <Button
                  variant="outline"
                  onClick={() => { setRemoving(null); setFailure(null); }}
                >
                  Cancel
                </Button>
                {blocked && removing.isActive && (
                  <Can permission={P.ACCOUNT_MANAGE}>
                    <Button
                      variant="outline"
                      onClick={() => {
                        void close(removing);
                        setRemoving(null);
                      }}
                    >
                      Close it instead
                    </Button>
                  </Can>
                )}
                <Button
                  variant="danger"
                  loading={deleteState.isLoading}
                  disabled={blocked}
                  onClick={() => void remove()}
                >
                  Delete it
                </Button>
              </>
            }
          >
            <div className="space-y-3">
              {/* The refusal belongs in front of the person, not on the page
                  behind this dialog. */}
              {failure && <Alert tone="danger">{failure}</Alert>}

              {used > 0 ? (
                <Alert tone="warning" title="This account has been used">
                  It carries {counts?.transactions ?? 0} ledger{' '}
                  {counts?.transactions === 1 ? 'entry' : 'entries'}
                  {(counts?.payments ?? 0) > 0 && `, ${counts?.payments} payment(s)`}
                  {(counts?.expenses ?? 0) > 0 && `, ${counts?.expenses} expense(s)`}
                  {(counts?.purchaseOrders ?? 0) > 0 && `, ${counts?.purchaseOrders} approved order(s)`}
                  . That history explains figures elsewhere, so deleting it would leave them
                  pointing at nothing. Close it instead — it keeps its record and takes no new
                  money.
                </Alert>
              ) : linked > 0 ? (
                <Alert tone="warning" title="Payment methods still point here">
                  {linked} payment method{linked === 1 ? '' : 's'} pays into {removing.name}.
                  Point {linked === 1 ? 'it' : 'them'} at another account first, under Payment
                  methods below, or payments through {linked === 1 ? 'it' : 'them'} would land
                  nowhere.
                </Alert>
              ) : (
                <Alert tone="warning">
                  Nothing has ever moved through this account, so it can be deleted outright.
                  This cannot be undone.
                </Alert>
              )}
            </div>
          </Modal>
        );
      })()}

      {counting && (
        <Modal
          open
          onClose={() => setCounting(null)}
          title={`Count ${counting.name}`}
          description="Enter what is actually there. The difference posts to the ledger."
          footer={
            <>
              <Button variant="outline" onClick={() => setCounting(null)}>Cancel</Button>
              <Button
                loading={adjustState.isLoading}
                disabled={!counted.amount || !counted.reason}
                onClick={() => void postCount()}
              >
                Post the difference
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Alert tone="info">
              The system says this account holds {formatMoney(counting.balance, format)}.
            </Alert>
            <Input
              label="Counted"
              type="number"
              min="0"
              step="0.01"
              value={counted.amount}
              onChange={(event) => setCounted({ ...counted, amount: event.target.value })}
            />
            <Input
              label="Reason"
              required
              value={counted.reason}
              onChange={(event) => setCounted({ ...counted, reason: event.target.value })}
              placeholder="Short at close — change given twice"
            />
          </div>
        </Modal>
      )}
    </>
  );
}
